import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { DeleteUserBlockedDialogContent } from '../../utils/delete-user-error.util';
import {
  ACCOUNT_SUSPENSION_REASONS,
  AccountSuspensionReason,
  suspensionReasonChoiceKey,
} from '../../utils/an-account-suspension';

/**
 * Shown when an account cannot be deleted yet. Since 22/10/2026 it also offers
 * to suspend the account until it can be (see an-account-suspension.ts):
 * before that, an administrator facing a minor's account with a paid activity
 * still to come could only close this dialog and leave the account usable.
 */
@Component({
  selector: 'bo-delete-user-blocked-dialog',
  imports: [TranslateModule, CommonModule, DialogModule],
  templateUrl: './delete-user-blocked-dialog.component.html',
  styleUrl: './delete-user-blocked-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeleteUserBlockedDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() content: DeleteUserBlockedDialogContent | null = null;
  /** Off by default, so a screen that has not wired the suspension does not show a button that does nothing. */
  @Input() canSuspend = false;
  @Input() alreadySuspended = false;
  @Input() suspending = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<void>();
  @Output() suspendRequested = new EventEmitter<AccountSuspensionReason>();

  readonly reasons = ACCOUNT_SUSPENSION_REASONS;
  readonly reasonChoiceKey = suspensionReasonChoiceKey;
  /** No reason is preselected: the administrator says why, every time. */
  reason: AccountSuspensionReason | null = null;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible'] && this.visible) {
      this.reason = null;
    }
  }

  chooseReason(reason: AccountSuspensionReason) {
    this.reason = reason;
  }

  suspend() {
    if (!this.reason || this.suspending) return;
    this.suspendRequested.emit(this.reason);
  }

  onVisibleChange(visible: boolean) {
    if (!visible) {
      this.closed.emit();
    }
    this.visibleChange.emit(visible);
  }

  onClose() {
    this.closed.emit();
    this.visibleChange.emit(false);
  }
}
